import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Form, Modal, Select, Space, Button, message } from 'udesk-ui';
import CommonList from '../smart-words-library-common-list';
import EllipsisLnline from 'src/components/common/ellipsis-inline';
import List, { TemplateProps as ListProps } from './components/List';
import { LanguageEnum, getLanguage } from 'Component/pages/components/LanguageSelect/const';
import { postTranslateTerminologyConfigs } from 'src/api/translateTerminologyConfigs';
import {
    putTranslateTerminologyConfigsById,
    getTranslateTerminologyConfigsById,
    deleteTranslateTerminologyConfigsById,
} from 'src/api/translateTerminologyConfigs/{id}';
import { TranslateTerminologyConfigCreateRequest } from 'src/api/types';

const StatusMap = {
    VALID: 1,
    INVALID: 0,
};

interface FormModel extends Pick<TranslateTerminologyConfigCreateRequest, 'items' | 'status'> {}

interface TemplateProps extends Record<string, any> {}

const Template: FC<TemplateProps> = (props) => {
    const listRef = useRef<any>();

    const [visible, setVisible] = useState(false);

    const [form] = Form.useForm<FormModel>();

    const [curId, setCurId] = useState<number>();

    const onOpen = useCallback(
        (id?: number) => {
            setCurId(id);
            setVisible(true);
            if (id) {
                getTranslateTerminologyConfigsById({ segments: { id } }).then((res) => {
                    form.setFieldsValue({
                        status: res.data?.status,
                        items: res.data?.items,
                    });
                });
            } else {
                form.setFieldsValue({
                    status: StatusMap.VALID,
                    items: [
                        {
                            lang: LanguageEnum.ZH,
                        },
                        {
                            lang: LanguageEnum.EN,
                        },
                    ],
                });
            }
        },
        [form]
    );

    const onDel = useCallback((id?: number) => {
        if (id) {
            Modal.confirm({
                title: /* 确定删除吗？ */ UdeskLocales['current'].pages.semanticIntelligence
                    .smartWordsLibrary.hotWords.index.areYouSureToDeleteIt,
                onOk: () => {
                    deleteTranslateTerminologyConfigsById({ segments: { id } })
                        .then(() => {
                            message.success(
                                /* 删除成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .smartWordsLibrary.hotWords.index.deleteSuccessful
                            );
                        })
                        .finally(() => {
                            listRef.current.actions.refresh?.();
                        });
                },
            });
        }
    }, []);

    const columns = useMemo(() => {
        return [
            {
                name: 'name',
                width: '20%',
                caption: /* 翻译术语 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.translationOfTerms,
            },
            {
                name: 'lang',
                width: '20%',
                caption: /* 语言 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.language,
                hasTemplate: true,
                getYieldContent: (name, item, index) => {
                    return (name || '')
                        .split(',')
                        .map((lang) => {
                            return getLanguage(Number(lang));
                        })
                        .join(',');
                },
            },
            {
                name: 'status',
                width: '10%',
                caption: /* 有效性 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.effectiveness,
                hasTemplate: true,
                getYieldContent: (name, item, index) => {
                    return name === 1 ? /* 是 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.yes : /* 否 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.no;
                },
            },
            {
                name: 'operation',
                width: '10%',
                caption:
                    /* 操作 */ UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary
                        .hotWords.index.operation,
                hasTemplate: true,
                getYieldContent: (name, item, index) => {
                    return (
                        <Space size="small">
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={() => onOpen(item.id)}
                            >{/* 编辑 */}{UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.edit}</Button>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                danger
                                onClick={() => onDel(item.id)}
                            >
                                {/* 删除 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .smartWordsLibrary.hotWords.index.delete
                                }
                            </Button>
                        </Space>
                    );
                },
            },
        ];
    }, [onDel, onOpen]);

    const [loading, setLoading] = useState(false);

    const onOk = () => {
        setLoading(true);
        form.validateFields()
            .then((values) => {
                if (curId) {
                    putTranslateTerminologyConfigsById(values, {
                        segments: { id: curId },
                    })
                        .then(() => {
                            message.success(
                                /* 保存成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .smartWordsLibrary.hotWords.index.saveSuccessful
                            );
                            onCancel();
                        })
                        .finally(() => {
                            listRef.current.actions.refresh?.();
                            setLoading(false);
                        });
                } else {
                    postTranslateTerminologyConfigs(values)
                        .then(() => {
                            message.success(
                                /* 保存成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .smartWordsLibrary.hotWords.index.saveSuccessful
                            );
                            onCancel();
                        })
                        .finally(() => {
                            listRef.current.actions.refresh?.();
                            setLoading(false);
                        });
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const onCancel = () => {
        setCurId(undefined);
        setVisible(false);
        form.setFieldsValue({
            status: StatusMap.VALID,
            items: undefined,
        });
    };

    return (
        <>
            <CommonList
                columns={columns}
                navItemType={Udesk.enums.smartWordsLibraryMenus.hotwords.id}
                getlistDataPath="translateTerminologyConfigs"
                openAddModal={onOpen}
                buttons={['add']}
                addText={/* 新建 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.new}
                onCreated={(instance) => {
                    listRef.current = instance;
                }}
                showLanguageSelect={false}
            />

            <Modal
                visible={visible}
                title={curId ? /* 编辑翻译术语 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.editTranslationTerms : /* 新建翻译术语 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.newTranslationTerm}
                onOk={onOk}
                okButtonProps={{ loading }}
                onCancel={onCancel}
                width={660}
            >
                <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={form}>
                    <Form.Item
                        label={<EllipsisLnline width="100%">{/* 有效性 */}{UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.effectiveness}</EllipsisLnline>}
                        name="status"
                    >
                        <Select
                            options={[
                                {
                                    label: /* 是 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.yes,
                                    value: StatusMap.VALID,
                                },
                                {
                                    label: /* 否 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.no,
                                    value: StatusMap.INVALID,
                                },
                            ]}
                            style={{ width: 200 }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={<EllipsisLnline width="100%">{/* 翻译术语 */}{UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.translationOfTerms}</EllipsisLnline>}
                        name="items"
                        rules={[
                            {
                                required: true,
                                validator: (rule, value: ListProps['value']) => {
                                    if (
                                        value &&
                                        value.filter((item) => item.lang && item.content).length >=
                                            2
                                    ) {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject(
                                            new Error(/* 请至少添加了两种语言的术语写法 */UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.translationTerminology.index.pleaseAddTermsInAtLeastTwoLanguages)
                                        );
                                    }
                                },
                            },
                        ]}
                    >
                        <List />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
